import React, { useEffect, useRef, useState } from 'react';

const HUBSPOT_SCRIPT_SRC = 'https://js.hsforms.net/forms/embed/v2.js';

function preconnectOnce() {
    if (typeof document === 'undefined') return;

    const urls = [
        'https://js.hsforms.net',
        'https://forms.hsforms.com',
        'https://forms-na1.hsforms.com', 
    ];

    urls.forEach((href) => {
        if (!document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
            const l = document.createElement('link');
            l.rel = 'preconnect';
            l.href = href;
            l.crossOrigin = '';
            document.head.appendChild(l);
        }
        if (!document.querySelector(`link[rel="dns-prefetch"][href="${href}"]`)) {
            const d = document.createElement('link');
            d.rel = 'dns-prefetch';
            d.href = href;
            document.head.appendChild(d);
        }
    });
}

function loadHubspotScriptOnce() {
    if (typeof window === 'undefined') return Promise.resolve();

    if (window.__hsFormScriptReady) return window.__hsFormScriptReady;

    preconnectOnce();

    window.__hsFormScriptReady = new Promise((resolve, reject) => {
        const ready = () => {
            if (window.hbspt?.forms?.create) {
                resolve();
                return true;
            }
            return false;
        };

        if (ready()) return;

        const existing = document.querySelector(`script[src="${HUBSPOT_SCRIPT_SRC}"]`);
        const onLoad = () => {
            if (!ready()) {
                const id = setInterval(() => {
                    if (ready()) clearInterval(id);
                }, 50);
                setTimeout(() => clearInterval(id), 3000);
            }
        };
        const onError = (e) => reject(e);

        if (existing) {
            existing.addEventListener('load', onLoad, { once: true });
            existing.addEventListener('error', onError, { once: true });
          
            const id = setInterval(() => {
                if (ready()) clearInterval(id);
            }, 50);
            setTimeout(() => clearInterval(id), 3000);
            return;
        }

        const script = document.createElement('script');
        script.src = HUBSPOT_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.referrerPolicy = 'origin';
        script.addEventListener('load', onLoad, { once: true });
        script.addEventListener('error', onError, { once: true });
        document.body.appendChild(script);

        const id = setInterval(() => {
            if (ready()) clearInterval(id);
        }, 50);
        setTimeout(() => clearInterval(id), 3000);
    });

    return window.__hsFormScriptReady;
}

function getSerializer() {
    if (typeof window === 'undefined') {
        return { get: () => null, set: () => { } };
    }
    if (!window.__hsFormInflight) {
        window.__hsFormInflight = new Map(); 
    }
    return {
        get: (key) => window.__hsFormInflight.get(key) || null,
        set: (key, promise) => {
            window.__hsFormInflight.set(key, promise);
            promise.finally(() => window.__hsFormInflight.delete(key));
        },
    };
}

function useVisible(ref, rootMargin = '200px') {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!ref.current || typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting || entry.intersectionRatio > 0) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { root: null, rootMargin, threshold: [0, 0.01, 0.1] }
        );
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [ref, rootMargin]);

    return visible;
}

const HubspotForm = ({ portalId, formId, region = 'na1', targetId }) => {
    const containerRef = useRef(null);
    const isVisible = useVisible(containerRef, '300px'); 

    useEffect(() => {
        let unmounted = false;

        async function ensureForm() {
            if (!isVisible) return;

            const key = `${portalId}-${formId}-${targetId}`;
            const serializer = getSerializer();

            const inFlight = serializer.get(key);
            if (inFlight) {
                await inFlight;
            }

            await loadHubspotScriptOnce();
            if (unmounted || !window.hbspt?.forms) return;

            const container = containerRef.current || document.getElementById(targetId);
            if (!container) return;

            const alreadyHasForm = !!container.querySelector('form.hs-form, .hs-form, iframe');
            if (alreadyHasForm) return;

            let release;
            const lock = new Promise((resolve) => (release = resolve));
            serializer.set(key, lock);

            try {
                if (container.querySelector('form.hs-form, .hs-form, iframe')) return;
                container.innerHTML = '';

                let settled = false;
                const settle = () => {
                    if (!settled) {
                        settled = true;
                        release();
                    }
                };

                const timeoutId = setTimeout(settle, 15000); 

                window.hbspt.forms.create({
                    portalId,
                    formId,
                    region,
                    target: `#${targetId}`,
                    onFormReady: () => {
                        clearTimeout(timeoutId);
                        const iframe = container.querySelector('iframe');
                        if (iframe) {
                            iframe.setAttribute('loading', 'lazy');
                            iframe.setAttribute('decoding', 'async');
                        }
                        settle();
                    },
                });
            } catch (e) {
                release();
                console.error('HubSpot form create failed:', e);
            }
        }

        ensureForm();

        return () => {
            unmounted = true;
        };
    }, [portalId, formId, region, targetId, isVisible]);

    return (
        <div id={targetId} ref={containerRef} style={{ minHeight: 80 }}> </div>
    );
};

export default HubspotForm;
